import { useMemo } from 'react';

import { useMerchant } from '@contexts/operator/MerchantContext';

import operatorApiFetch from '@services/operator/apiService';

/**
 * Operator Inbound Service Hook
 *
 * 接收 merchantId 作為參數，並注入到相關 API 呼叫中
 *
 * @param {Object} options - 選項
 * @param {number} options.merchantId - Merchant ID（可選，如果未提供則從 MerchantContext 獲取）
 * @returns {Object} inboundService，部分方法自動帶入 merchant_id
 *
 * @example
 * const inboundService = useOperatorInboundService({ merchantId: 123 });
 *
 * // merchant_id 會自動加入到相關 request
 * await inboundService.create({ name: 'Inbound A', inbound_type: 'receiving' });
 * await inboundService.getById(1);
 */
export function useOperatorInboundService(options = {}) {
  const merchantContext = useMerchant();
  const contextMerchantId = merchantContext?.merchantId;
  const merchantId = options.merchantId ?? contextMerchantId;  // 傳入優先，否則用 context

  return useMemo(() => ({
    // 獲取入庫單列表（用於表格）
    getRecords: async (params = {}) => {
      const res = await operatorApiFetch('inbounds/get_records', {
        method: 'POST',
        body: {
          ...params,
          merchant_id: merchantId
        }
      });
      return res.json();
    },

    // 獲取所有入庫單 ID（用於篩選）
    getIds: async (params = {}) => {
      const res = await operatorApiFetch('inbounds/get_ids', {
        method: 'POST',
        body: {
          ...params,
          merchant_id: merchantId
        }
      });
      return res.json();
    },

    // 獲取單個入庫單
    get: async (id) => {
      const res = await operatorApiFetch(`inbounds/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // 獲取單個入庫單（別名）
    getById: async (id) => {
      const res = await operatorApiFetch(`inbounds/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // 創建入庫單（自動注入 merchant_id）
    create: async (data) => {
      const res = await operatorApiFetch('inbounds', {
        method: 'POST',
        body: {
          inbound: {
            merchant_id: merchantId,
            ...data,
          }
        }
      });
      return res.json();
    },

    // 更新入庫單
    update: async (id, data) => {
      const res = await operatorApiFetch(`inbounds/${id}`, {
        method: 'PUT',
        body: {
          inbound: {
            ...data,
          }
        }
      });
      return res.json();
    },

    // 取消入庫單
    cancel: async (id) => {
      const res = await operatorApiFetch(`inbounds/${id}/cancel`, {
        method: 'POST'
      });
      return res.json();
    },

    // 恢復入庫單
    resume: async (id) => {
      const res = await operatorApiFetch(`inbounds/${id}/resume`, {
        method: 'POST'
      });
      return res.json();
    },

    // 匯出入庫單
    export: async (inboundIds) => {
      const res = await operatorApiFetch('inbounds/export', {
        method: 'POST',
        body: { inbound_ids: inboundIds }
      });
      return res.json();
    },

    // 獲取序號
    getSerialNumbers: async (inboundId, params = {}) => {
      const res = await operatorApiFetch(`inbounds/${inboundId}/serial_numbers_get_records`, {
        method: 'POST',
        body: { ...params }
      });
      return res.json();
    },

    // 獲取上架紀錄
    getShelfMovements: async (inboundId, params = {}) => {
      const res = await operatorApiFetch('shelf_movements/get_records', {
        method: 'POST',
        body: {
          ...params,
          inbound_id: inboundId
        }
      });
      return res.json();
    },

    // 產生 PDF
    generatePdf: async (id) => {
      const res = await operatorApiFetch(`inbounds/${id}/generate_pdf`, {
        method: 'POST'
      });
      return res.json();
    },

    // 產生入庫標籤（POST inbounds/:id/generate_labels → 觸發背景 worker，回傳 job_id）
    generateLabels: async (id) => {
      const res = await operatorApiFetch(`inbounds/${id}/generate_labels`, {
        method: 'POST'
      });
      return res.json();
    },

    // 建議入庫單號
    suggestName: async (merchantIdParam = null) => {
      const id = merchantIdParam ?? merchantId;
      const res = await operatorApiFetch('inbounds/suggest_name', {
        method: 'GET',
        params: { merchant_id: id }
      });
      return res.json();
    },

    // 加值服務 - 新增
    addValueAddedService: async (inboundId, data) => {
      const res = await operatorApiFetch(`inbounds/${inboundId}/value_added_services`, {
        method: 'POST',
        body: { value_added_service: data }
      });
      return res.json();
    },

    // 加值服務 - 更新
    updateValueAddedService: async (inboundId, serviceId, data) => {
      const res = await operatorApiFetch(`inbounds/${inboundId}/value_added_services/${serviceId}`, {
        method: 'PATCH',
        body: { value_added_service: data }
      });
      return res.json();
    },

    // 加值服務 - 刪除
    deleteValueAddedService: async (inboundId, serviceId) => {
      const res = await operatorApiFetch(`inbounds/${inboundId}/value_added_services/${serviceId}`, {
        method: 'DELETE'
      });
      return res.json();
    }
  }), [merchantId]);
}
